import {
  HttpEventType,
  HttpProgressEvent,
  HttpResponse,
  HttpSentEvent,
} from '@angular/common/http';
import { inject, Injector } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { isWSStatusErrorResponse } from '@qtek/core/websockets-core';
import { ErrorSnackBarData, SnackBarService } from '@qtek/libs/snack-bar-core';
import {
  DocNode,
  DOCUMENT_UPLOAD_CANCELED,
  DOCUMENT_UPLOAD_COMPLETED,
  DOCUMENT_UPLOAD_CREATED,
  DOCUMENT_UPLOAD_STARTED,
  DocumentType,
  PlaceholderTokenPayload,
  PreUploadPayload,
  PreUploadState,
} from '@qtek/shared/models';
import {
  catchError,
  EMPTY,
  filter,
  map,
  mergeMap,
  Observable,
  of,
  pipe,
  Subject,
  switchMap,
  takeUntil,
  tap,
  throwError,
} from 'rxjs';
import { UserStore } from '../user/user.store';
import { DocumentScanGate } from './document-scan-gate.service';
import { DocumentsService } from './documents.service';
import {
  applyDeleteDocumentSuccess,
  applyDocumentUpsert,
  applyGetEntityDocumentsSuccess,
  applyUploadDocumentsSuccess,
  documentsInitialState,
} from './documents.updaters';

export const DocumentsStore = signalStore(
  { providedIn: 'root' },
  withState(documentsInitialState),
  withMethods(
    (
      store,
      service = inject(DocumentsService),
      scanGate = inject(DocumentScanGate),
      userStore = inject(UserStore),
      snackBar = inject(SnackBarService),
      injector = inject(Injector)
    ) => {
      const disconnect$ = new Subject<void>();

      return {
        // ── WS: data room documents list (multiple concurrent channels) ────────
        connect: rxMethod<{ refId: string; _attrs: any }>(
          pipe(
            tap(() =>
              patchState(store, { isDataRoomEntityDocumentsLoading: true })
            ),
            mergeMap(payload =>
              service.subscribe({ prms: payload }).pipe(
                takeUntil(disconnect$),
                tap(({ op, res, sts }: any) => {
                  switch (op) {
                    case 'query':
                      if (isWSStatusErrorResponse(sts)) {
                        patchState(store, {
                          isDataRoomEntityDocumentsLoading: false,
                        });
                      } else {
                        patchState(store, {
                          dataRoomEntityDocuments: res ?? [],
                          isDataRoomEntityDocumentsLoading: false,
                          dataRoomEntityMeta: sts?.meta || null,
                        });
                      }
                      break;
                    case 'ins':
                      if (res) {
                        if (res.lastToken) {
                          patchState(store, {
                            placeholderToken: { tkn: res.lastToken },
                          });
                        }
                        const isUploading = store
                          .preUploadDocuments()
                          .some(
                            p =>
                              p.docId === res._id &&
                              (p.status === DOCUMENT_UPLOAD_CREATED ||
                                p.status === DOCUMENT_UPLOAD_STARTED)
                          );
                        if (!isUploading) {
                          patchState(store, state => ({
                            dataRoomEntityDocuments:
                              state.dataRoomEntityDocuments.concat({
                                ...res,
                              }),
                          }));
                        }
                      }
                      break;
                    case 'upd':
                      if (res) {
                        patchState(store, state =>
                          applyDocumentUpsert(state, res)
                        );
                      } else if (isWSStatusErrorResponse(sts)) {
                        patchState(store, state => ({
                          updateDocumentWSFailureCount:
                            state.updateDocumentWSFailureCount + 1,
                        }));
                      }
                      break;
                    case 'del':
                      if (res) {
                        patchState(store, state => ({
                          dataRoomEntityDocuments:
                            state.dataRoomEntityDocuments.filter(
                              (item: any) => item._id !== res._id
                            ),
                          preUploadDocuments: state.preUploadDocuments.filter(
                            item => item.docId !== res._id
                          ),
                        }));
                      }
                      break;
                    default:
                      break;
                  }
                })
              )
            )
          )
        ),

        disconnect(): void {
          disconnect$.next();
          patchState(store, {
            dataRoomEntityDocuments: [],
            dataRoomEntityMeta: null,
          });
        },

        updateDocumentWS(payload: any): void {
          service.updateDocumentWS(payload);
        },

        deleteDocumentWS(payload: any): void {
          service.deleteDocumentWS(payload);
        },

        createDocumentPlaceholderWS(payload: any): void {
          service.createDocumentPlaceholderWS(payload);
        },

        getEntityDocumentsWS(payload?: any): void {
          service.getItemDocumentsWs(payload);
        },

        // ── HTTP: upload (also folds the personal-avatar scan-gate flow) ───────
        uploadDocuments(payload: {
          data: FormData;
          attr?: DocumentType;
          id?: string;
        }): Observable<DocNode[]> {
          return service.uploadDocuments(payload.data, payload.attr).pipe(
            filter((event: any) => event.type === HttpEventType.Response),
            map((event: HttpResponse<{ res: DocNode } | DocNode>) => {
              const body = event.body;
              const doc =
                body && typeof body === 'object' && 'res' in body
                  ? (body as { res: DocNode }).res
                  : (body as DocNode);
              return [{ ...(doc || {}), attr: payload.attr } as DocNode];
            }),
            switchMap(docs => {
              patchState(store, state =>
                applyUploadDocumentsSuccess(state, docs)
              );
              if (payload.attr !== DocumentType.PERSONAL_AVATAR) {
                return of(docs);
              }

              const avatarDoc = docs[0];
              const prsId = userStore.currentPerson()?.prsId;
              const settled$ = prsId
                ? scanGate.whenSettled({
                    docId: avatarDoc._id,
                    refId: `prs=${prsId}`,
                    attr: DocumentType.PERSONAL_AVATAR,
                  })
                : of('ready' as const);

              return settled$.pipe(
                tap(outcome => {
                  if (outcome === 'ready') {
                    patchState(store, { personalAvatar: avatarDoc._id });
                  } else {
                    snackBar.showSnackBar({
                      type: 'ErrorMessage',
                      message: 'ERR_IMAGE_LOAD_FAILED',
                    } as ErrorSnackBarData);
                  }
                }),
                map(() => docs)
              );
            }),
            catchError(error => {
              if (payload.attr === DocumentType.PERSONAL_AVATAR) {
                snackBar.showSnackBar({
                  type: 'ErrorMessage',
                  message: 'ERR_FILE_UPLOAD_ERR',
                } as ErrorSnackBarData);
              }
              return throwError(() => error);
            })
          );
        },

        // ── HTTP: fire-and-forget personal document upload (no result needed) ──
        uploadPersonalDocument: rxMethod<FormData>(
          pipe(
            mergeMap(data =>
              service
                .uploadDocuments(data, DocumentType.PERSONAL_DOCUMENTS)
                .pipe(
                  filter((event: any) => event.type === HttpEventType.Response),
                  map((event: HttpResponse<{ res: DocNode } | DocNode>) => {
                    const body = event.body;
                    const doc =
                      body && typeof body === 'object' && 'res' in body
                        ? (body as { res: DocNode }).res
                        : (body as DocNode);
                    return [
                      {
                        ...(doc || {}),
                        attr: DocumentType.PERSONAL_DOCUMENTS,
                      } as DocNode,
                    ];
                  }),
                  tap(docs =>
                    patchState(store, state =>
                      applyUploadDocumentsSuccess(state, docs)
                    )
                  ),
                  catchError(() => of(null))
                )
            )
          )
        ),

        // ── HTTP: pre-upload token + tracked 2-stage upload ─────────────────────
        preUploadAndTrack: rxMethod<{ data: PreUploadPayload; file: File }>(
          pipe(
            mergeMap(
              ({ data, file }) =>
                service.preUploadDocument(data).pipe(
                  catchError(() => {
                    patchState(store, state => ({
                      preUploadTokenFailureCount:
                        state.preUploadTokenFailureCount + 1,
                    }));
                    return EMPTY;
                  }),
                  tap(({ res }) => {
                    const { tkn, docIds, refId } = res;
                    patchState(store, state => ({
                      preUploadDocuments: state.preUploadDocuments.concat({
                        tkn,
                        docId: docIds[0],
                        refId,
                        status: DOCUMENT_UPLOAD_CREATED,
                        filename: file.name,
                        filesize: file.size,
                      }),
                    }));
                  }),
                  switchMap(({ res }) => {
                    const formData = new FormData();
                    formData.append('data', file);
                    formData.append('tkn', res.tkn);

                    let timestamp = Date.now();
                    let lastLoaded = 0;

                    return service.uploadDocuments(formData).pipe(
                      takeUntil(
                        toObservable(store.preUploadDocuments, {
                          injector,
                        }).pipe(
                          map(docs => docs.find(doc => doc.tkn === res.tkn)),
                          filter(
                            (doc): doc is PreUploadState =>
                              doc?.status === DOCUMENT_UPLOAD_CANCELED
                          )
                        )
                      ),
                      filter(
                        (event: any) =>
                          event.type === HttpEventType.Response ||
                          event.type === HttpEventType.UploadProgress ||
                          event.type === HttpEventType.Sent
                      ),
                      tap(
                        (
                          event:
                            | HttpResponse<DocNode>
                            | HttpProgressEvent
                            | HttpSentEvent
                        ) => {
                          switch (event.type) {
                            case HttpEventType.Sent:
                              patchState(store, state => ({
                                preUploadDocuments:
                                  state.preUploadDocuments.map(doc =>
                                    doc.tkn === res.tkn
                                      ? {
                                          ...doc,
                                          status: DOCUMENT_UPLOAD_STARTED,
                                        }
                                      : doc
                                  ),
                              }));
                              break;
                            case HttpEventType.UploadProgress: {
                              const loaded = event.loaded;
                              const time = Date.now() - timestamp;
                              const percent = (loaded - lastLoaded) / 100;
                              const chunk = percent * file.size;
                              const speed = (chunk / (time / 1000)).toFixed(2);

                              timestamp = Date.now();
                              lastLoaded = loaded;

                              patchState(store, state => ({
                                preUploadDocuments:
                                  state.preUploadDocuments.map(doc =>
                                    doc.tkn === res.tkn
                                      ? {
                                          ...doc,
                                          progress: Math.round(
                                            (100 * event.loaded) / file.size
                                          ),
                                          speed: parseFloat(speed),
                                        }
                                      : doc
                                  ),
                              }));
                              break;
                            }
                            case HttpEventType.Response: {
                              patchState(store, state => ({
                                preUploadDocuments:
                                  state.preUploadDocuments.map(doc =>
                                    doc.tkn === res.tkn
                                      ? {
                                          ...doc,
                                          status: DOCUMENT_UPLOAD_COMPLETED,
                                        }
                                      : doc
                                  ),
                              }));
                              const doc = event.body;
                              if (doc) {
                                patchState(store, state =>
                                  applyDocumentUpsert(state, doc)
                                );
                              }
                              break;
                            }
                            default:
                              break;
                          }
                        }
                      ),
                      catchError(() => {
                        patchState(store, state => ({
                          preUploadDocuments: state.preUploadDocuments
                            .map(doc =>
                              doc.tkn === res.tkn
                                ? { ...doc, status: DOCUMENT_UPLOAD_CANCELED }
                                : doc
                            )
                            .filter(
                              doc => !(res.docIds ?? []).includes(doc.docId)
                            ),
                        }));
                        const docId = res.docIds?.[0];
                        if (docId && res.refId) {
                          service.deleteDocumentWS({
                            id: `${res.refId}#${docId}`,
                          });
                        }
                        return of(null);
                      })
                    );
                  })
                ),
              5
            )
          )
        ),

        updatePreUploadDocument(
          tkn: string,
          toUpdate: Partial<PreUploadState>
        ): void {
          patchState(store, state => ({
            preUploadDocuments: state.preUploadDocuments.map(doc =>
              doc.tkn === tkn ? { ...doc, ...toUpdate } : doc
            ),
          }));
        },

        deletePreUploadDocumentsByIds(ids: string[]): void {
          patchState(store, state => ({
            preUploadDocuments: state.preUploadDocuments.filter(
              document => !ids?.includes(document.docId)
            ),
          }));
        },

        deletePreUploadDocumentsByRef(refId: string): void {
          patchState(store, state => ({
            preUploadDocuments: state.preUploadDocuments.filter(
              document => document.refId !== refId
            ),
          }));
        },

        // ── HTTP: delete ────────────────────────────────────────────────────────
        deleteDocument(payload: {
          docId: string;
          attr: DocumentType;
          refId: string;
        }): Observable<void> {
          return service.deleteDocument(payload.docId, payload.refId).pipe(
            tap(() =>
              patchState(store, state =>
                applyDeleteDocumentSuccess(state, payload)
              )
            ),
            map((): void => undefined)
          );
        },

        // ── HTTP: entity documents / logos / logo / misc ────────────────────────
        getEntityDocuments: rxMethod<
          { refId?: string; type?: DocumentType } | undefined
        >(
          pipe(
            mergeMap(payload => {
              const refId =
                payload?.refId || `prs=${userStore.currentPerson()?.prsId}`;
              return service.getDocumentsOfEntity(refId, payload?.type).pipe(
                tap(({ res }) =>
                  patchState(store, state =>
                    applyGetEntityDocumentsSuccess(state, res)
                  )
                ),
                catchError(() => {
                  patchState(store, {
                    isDataRoomEntityDocumentsLoading: false,
                  });
                  return of(null);
                })
              );
            })
          )
        ),

        getLogos: rxMethod<void>(
          pipe(
            mergeMap(() =>
              service.getLogoTemplates().pipe(
                tap(({ res }) => patchState(store, { creatorLogos: res })),
                catchError(() => of(null))
              )
            )
          )
        ),

        updateCompanyLogo: rxMethod<any>(
          pipe(
            mergeMap(payload =>
              service.updateLogo(payload).pipe(
                tap(({ res }) =>
                  patchState(store, state => ({
                    companyLogo: res?._id ?? state.companyLogo,
                    companyLogoCacheBuster: state.companyLogoCacheBuster + 1,
                  }))
                ),
                catchError(() => of(null))
              )
            )
          )
        ),

        sendDocument: rxMethod<{ id: string; destination: string }>(
          pipe(
            mergeMap(payload =>
              service
                .sendDocument(payload.id, payload.destination)
                .pipe(catchError(() => of(null)))
            )
          )
        ),

        updateDocumentDescription: rxMethod<{
          id: string;
          description: string;
        }>(
          pipe(
            mergeMap(payload =>
              service
                .updateDocumentDescription(payload.id, payload.description)
                .pipe(catchError(() => of(null)))
            )
          )
        ),

        bulkDelete: rxMethod<{ _id: string; tp: 1 | 50 }[]>(
          pipe(
            mergeMap(payload =>
              service.bulkDelete(payload).pipe(catchError(() => of(null)))
            )
          )
        ),

        applyTemplate: rxMethod<{ templateData: string; dealId: string }>(
          pipe(
            mergeMap(payload =>
              service
                .submitSelectedTemplate(payload.templateData, payload.dealId)
                .pipe(catchError(() => of(null)))
            )
          )
        ),

        generatePlaceholderToken: rxMethod<PlaceholderTokenPayload>(
          pipe(
            mergeMap(payload =>
              service.generatePlaceholderToken(payload).pipe(
                tap(({ res }) => patchState(store, { placeholderToken: res })),
                catchError(() => {
                  patchState(store, { placeholderToken: null });
                  patchState(store, state => ({
                    placeholderTokenFailureCount:
                      state.placeholderTokenFailureCount + 1,
                  }));
                  return of(null);
                })
              )
            )
          )
        ),

        clearPlaceholderToken(): void {
          patchState(store, { placeholderToken: null });
        },

        resetCorporateNda(): void {
          patchState(store, { corporateNda: null });
        },

        // ── Folder ops (called by category.effects.ts on its own WS channel) ────
        applyFolderCreated(doc: DocNode): void {
          patchState(store, state => ({
            dataRoomEntityDocuments: state.dataRoomEntityDocuments.concat(doc),
          }));
        },

        applyFolderUpdated(doc: DocNode): void {
          patchState(store, state => applyDocumentUpsert(state, doc));
        },

        applyFolderDeleted(doc: DocNode): void {
          patchState(store, state => ({
            dataRoomEntityDocuments: state.dataRoomEntityDocuments.filter(
              folder => folder._id !== doc._id
            ),
          }));
        },
      };
    }
  )
);
